import React, { MutableRefObject } from "react";
import { Block, HiVis, Design } from "@/types/editor/blocks";
import { TransformHandle } from "./types";
import { cloneHiVis } from "@/lib/hivis-manager";

export interface InteractionDependencies {
  // Functions
  calculateRect: () => { x: number; y: number; width: number; height: number };
  getHandles: (rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => TransformHandle[];
  convertToCanvasCoords: (x: number, y: number) => { x: number; y: number };
  getSelectedBlockBounds: () => {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  findClickedBlock: (x: number, y: number, block: Block) => number[] | null;

  // Refs
  canvasRef: React.RefObject<HTMLCanvasElement>;
  throttleRef: MutableRefObject<NodeJS.Timeout | undefined>;
  pendingUpdateRef: MutableRefObject<{
    width?: number;
    height?: number;
    timestamp: number;
    isProcessing?: boolean;
  } | null>;
  animationFrameRef: MutableRefObject<number | undefined>;
  renderTimeoutRef: MutableRefObject<number | undefined>;
  drawRef: MutableRefObject<(() => void) | undefined>;

  // State values
  blocks: Block[];
  width: number;
  height: number;
  containerSize: number;
  SCALE: number;
  HANDLE_SIZE: number;
  MOBILE_HANDLE_SIZE: number;
  isSelected: boolean;
  isDragging: boolean;
  activeHandle: string | null;
  startPos: { x: number; y: number };
  isMobile: boolean;
  rectBounds: { x: number; y: number; width: number; height: number };
  selectedPath: number[];
  enableTransform: boolean;
  hiVis: HiVis | null;

  // State setters
  setIsSelected: (selected: boolean) => void;
  setIsDragging: (dragging: boolean) => void;
  setActiveHandle: (handle: string | null) => void;
  setStartPos: (pos: { x: number; y: number }) => void;
  setIsInteracting: (interacting: boolean) => void;
  setHasUpdated: (updated: boolean) => void;
  onHiVisChange?: (hiVis: HiVis) => void;
  onSelect?: (path: number[]) => void;
}

export interface InteractionHandlers {
  handleStart: (x: number, y: number) => boolean;
  handleMove: (x: number, y: number) => void;
  handleEnd: () => void;
  handleMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  throttledMove: (x: number, y: number) => void;
  handleCanvasClick: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  updateDimensions: (width?: number, height?: number) => void;
}

export const createInteractionHandlers = (
  deps: InteractionDependencies
): InteractionHandlers => {
  const handleStart = (x: number, y: number): boolean => {
    const canvas = deps.canvasRef.current;
    if (!canvas || !deps.enableTransform) return false;

    deps.setIsInteracting(true);

    const coords = deps.convertToCanvasCoords(x, y);
    const rect = deps.calculateRect();
    const touchPadding = deps.isMobile ? 20 : 10;

    // Check if clicked on handle first when selected
    if (deps.isSelected) {
      const handles = deps.getHandles(rect);
      const clickedHandle = handles.find(
        (handle) =>
          Math.abs(handle.x - coords.x) <= touchPadding &&
          Math.abs(handle.y - coords.y) <= touchPadding
      );

      if (clickedHandle) {
        deps.setIsDragging(true);
        deps.setActiveHandle(clickedHandle.action);
        deps.setStartPos(coords);
        if (canvas.style) canvas.style.cursor = clickedHandle.cursor;
        return true;
      }
    }

    // Check if clicked on rectangle
    if (
      coords.x >= rect.x &&
      coords.x <= rect.x + rect.width &&
      coords.y >= rect.y &&
      coords.y <= rect.y + rect.height
    ) {
      deps.setIsSelected(true);
    } else {
      deps.setIsSelected(false);
    }
    return false;
  };

  const handleMove = (x: number, y: number): void => {
    const canvas = deps.canvasRef.current;
    if (!canvas || !deps.enableTransform) return;

    const coords = deps.convertToCanvasCoords(x, y);

    if (deps.isDragging && deps.activeHandle) {
      const dx = coords.x - deps.startPos.x;
      const dy = coords.y - deps.startPos.y;

      // Get the selected block's dimensions and bounds
      const selectedBlock =
        deps.selectedPath && deps.selectedPath.length > 0
          ? (() => {
              let block = deps.blocks?.[deps.selectedPath[0]];
              for (let i = 1; i < deps.selectedPath.length; i++) {
                if (!block?.blocks) break;
                block = block.blocks[deps.selectedPath[i]];
              }
              return block;
            })()
          : deps.blocks?.[0] || null;

      // Get the parent block to calculate ratios
      const parentBlock =
        deps.selectedPath && deps.selectedPath.length > 1
          ? (() => {
              let block = deps.blocks?.[deps.selectedPath[0]];
              for (let i = 1; i < deps.selectedPath.length - 1; i++) {
                if (!block?.blocks) break;
                block = block.blocks[deps.selectedPath[i]];
              }
              return block;
            })()
          : null;

      const currentWidth =
        deps.selectedPath.length === 0
          ? deps.width
          : selectedBlock?.width || deps.width;
      const currentHeight =
        deps.selectedPath.length === 0
          ? deps.height
          : selectedBlock?.height || deps.height;

      // Calculate scale factors based on the selected block's bounds
      const selectedBounds = deps.getSelectedBlockBounds();
      const scaleFactorWidth =
        (selectedBounds.width / deps.containerSize) * deps.SCALE;
      const scaleFactorHeight =
        (selectedBounds.height / deps.containerSize) * deps.SCALE;

      // Calculate new dimensions based on handle being dragged
      let newWidth = currentWidth;
      let newHeight = currentHeight;

      switch (deps.activeHandle) {
        case "e":
          newWidth = Math.round(
            Math.max(50, currentWidth + dx * scaleFactorWidth)
          );
          if (selectedBlock && parentBlock) {
            const widthRatio = Number(
              (newWidth / parentBlock.width).toFixed(4)
            );
            selectedBlock.widthRatio = widthRatio;
          }
          updateDimensions(newWidth);
          break;
        case "w":
          newWidth = Math.round(
            Math.max(50, currentWidth - dx * scaleFactorWidth)
          );
          if (selectedBlock && parentBlock) {
            const widthRatio = Number(
              (newWidth / parentBlock.width).toFixed(4)
            );
            selectedBlock.widthRatio = widthRatio;
          }
          updateDimensions(newWidth);
          break;
        case "n":
          newHeight = Math.round(
            Math.max(50, currentHeight - dy * scaleFactorHeight)
          );
          if (selectedBlock && parentBlock) {
            const heightRatio = Number(
              (newHeight / parentBlock.height).toFixed(4)
            );
            selectedBlock.heightRatio = heightRatio;
          }
          updateDimensions(undefined, newHeight);
          break;
        case "s":
          newHeight = Math.round(
            Math.max(50, currentHeight + dy * scaleFactorHeight)
          );
          if (selectedBlock && parentBlock) {
            const heightRatio = Number(
              (newHeight / parentBlock.height).toFixed(4)
            );
            selectedBlock.heightRatio = heightRatio;
          }
          updateDimensions(undefined, newHeight);
          break;
        case "ne": {
          newWidth = Math.round(
            Math.max(50, currentWidth + dx * scaleFactorWidth)
          );
          newHeight = Math.round(
            Math.max(50, currentHeight - dy * scaleFactorHeight)
          );
          if (selectedBlock && parentBlock) {
            const widthRatio = Number(
              (newWidth / parentBlock.width).toFixed(4)
            );
            const heightRatio = Number(
              (newHeight / parentBlock.height).toFixed(4)
            );
            selectedBlock.widthRatio = widthRatio;
            selectedBlock.heightRatio = heightRatio;
          }
          updateDimensions(newWidth, newHeight);
          break;
        }
        case "nw": {
          newWidth = Math.round(
            Math.max(50, currentWidth - dx * scaleFactorWidth)
          );
          newHeight = Math.round(
            Math.max(50, currentHeight - dy * scaleFactorHeight)
          );
          if (selectedBlock && parentBlock) {
            const widthRatio = Number(
              (newWidth / parentBlock.width).toFixed(4)
            );
            const heightRatio = Number(
              (newHeight / parentBlock.height).toFixed(4)
            );
            selectedBlock.widthRatio = widthRatio;
            selectedBlock.heightRatio = heightRatio;
          }
          updateDimensions(newWidth, newHeight);
          break;
        }
        case "se": {
          newWidth = Math.round(
            Math.max(50, currentWidth + dx * scaleFactorWidth)
          );
          newHeight = Math.round(
            Math.max(50, currentHeight + dy * scaleFactorHeight)
          );
          if (selectedBlock && parentBlock) {
            const widthRatio = Number(
              (newWidth / parentBlock.width).toFixed(4)
            );
            const heightRatio = Number(
              (newHeight / parentBlock.height).toFixed(4)
            );
            selectedBlock.widthRatio = widthRatio;
            selectedBlock.heightRatio = heightRatio;
          }
          updateDimensions(newWidth, newHeight);
          break;
        }
        case "sw": {
          newWidth = Math.round(
            Math.max(50, currentWidth - dx * scaleFactorWidth)
          );
          newHeight = Math.round(
            Math.max(50, currentHeight + dy * scaleFactorHeight)
          );
          if (selectedBlock && parentBlock) {
            const widthRatio = Number(
              (newWidth / parentBlock.width).toFixed(4)
            );
            const heightRatio = Number(
              (newHeight / parentBlock.height).toFixed(4)
            );
            selectedBlock.widthRatio = widthRatio;
            selectedBlock.heightRatio = heightRatio;
          }
          updateDimensions(newWidth, newHeight);
          break;
        }
      }

      // Update the start position for the next move
      deps.setStartPos(coords);
    } else if (deps.isSelected) {
      // Update cursor based on hover over handles
      const handles = deps.getHandles(deps.rectBounds);
      const touchPadding = deps.isMobile
        ? deps.MOBILE_HANDLE_SIZE
        : deps.HANDLE_SIZE;
      const hoverHandle = handles.find(
        (handle) =>
          Math.abs(handle.x - coords.x) <= touchPadding &&
          Math.abs(handle.y - coords.y) <= touchPadding
      );

      if (canvas.style) {
        canvas.style.cursor = hoverHandle ? hoverHandle.cursor : "default";
      }
    }
  };

  const handleEnd = (): void => {
    deps.setIsInteracting(false);
    deps.setIsDragging(false);
    deps.setActiveHandle(null);

    const canvas = deps.canvasRef.current;
    if (canvas?.style) canvas.style.cursor = "default";
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    const shouldPrevent = handleStart(e.clientX, e.clientY);
    if (shouldPrevent) {
      e.preventDefault();
    }
  };

  const throttledMove = (x: number, y: number): void => {
    if (deps.throttleRef.current) return;

    deps.throttleRef.current = setTimeout(
      () => {
        handleMove(x, y);
        deps.throttleRef.current = undefined;
      },
      deps.isMobile ? 50 : 25
    );
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    if (!deps.blocks || deps.blocks.length === 0 || !deps.onSelect) return;

    const coords = deps.convertToCanvasCoords(e.clientX, e.clientY);
    const clickedPath = deps.findClickedBlock(
      coords.x,
      coords.y,
      deps.blocks[0]
    );

    if (clickedPath) {
      deps.onSelect(clickedPath);
    }
  };

  const updateDimensions = (newWidth?: number, newHeight?: number): void => {
    const now = Date.now();

    // Update the pending values
    deps.pendingUpdateRef.current = {
      width: newWidth ?? deps.pendingUpdateRef.current?.width,
      height: newHeight ?? deps.pendingUpdateRef.current?.height,
      timestamp: now,
      isProcessing: true,
    };

    // Cancel any existing animation frame and timeout
    if (deps.animationFrameRef.current) {
      cancelAnimationFrame(deps.animationFrameRef.current);
    }
    if (deps.renderTimeoutRef.current) {
      clearTimeout(deps.renderTimeoutRef.current);
    }

    // Set hasUpdated to true to ensure a render will happen
    deps.setHasUpdated(true);

    // Render immediately to show feedback during drag operation
    deps.drawRef.current?.();

    // Debounce the actual dimension update to prevent too many state changes
    if (deps.throttleRef.current) {
      clearTimeout(deps.throttleRef.current);
    }

    deps.throttleRef.current = setTimeout(() => {
      const pending = deps.pendingUpdateRef.current;
      if (!pending) return;

      // Process dimension update
      if (
        deps.onHiVisChange &&
        (pending.width !== undefined || pending.height !== undefined) &&
        deps.hiVis?.design
      ) {
        // Create a deep copy of the design to avoid mutating the original
        const designCopy: Design = {
          ...deps.hiVis.design,
          name: deps.hiVis.design.name || "",
          global_gap: deps.hiVis.design.global_gap || 0,
          blocks: [...(deps.hiVis.design.blocks || [])],
          thumbnail: deps.hiVis.design.thumbnail || "",
        };

        // Update root block dimensions if they changed
        if (pending.width !== undefined && designCopy.blocks?.[0]) {
          designCopy.blocks[0] = {
            ...designCopy.blocks[0],
            width: pending.width,
          };
        }

        if (pending.height !== undefined && designCopy.blocks?.[0]) {
          designCopy.blocks[0] = {
            ...designCopy.blocks[0],
            height: pending.height,
          };
        }

        // Create updated hiVis and propagate changes
        const updatedHiVis = cloneHiVis(deps.hiVis);
        updatedHiVis.design = designCopy;

        // Apply changes to the hiVis design
        deps.onHiVisChange(updatedHiVis);
      }

      // Mark update as complete and schedule a final render
      deps.pendingUpdateRef.current = {
        ...pending,
        isProcessing: false,
      };

      // Schedule final render after state update
      setTimeout(() => {
        deps.setHasUpdated(true);
        deps.drawRef.current?.();
        deps.throttleRef.current = undefined;
      }, 10);
    }, 50);
  };

  return {
    handleStart,
    handleMove,
    handleEnd,
    handleMouseDown,
    throttledMove,
    handleCanvasClick,
    updateDimensions,
  };
};
